// The booking's own pages on the booking site, opened from the WhatsApp
// confirmation card (web_whatsapp_v2): /:hospital_id/status/:booking_id and
// /:hospital_id/manage/:booking_id. The link carries a booking-scoped token
// (?t=) minted by the bot for the phone that owns the booking; no OTP.
//
// The booking summary, the live queue position on the status page, the fee
// when it is still unpaid, and on the manage page cancel / reschedule (each
// switched on per hospital, with an optional cutoff; the server decides and
// says why not). The token is kept per tab so a refresh keeps working after
// the address bar is cleaned.
import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import moment from "moment-timezone";
import { FaWhatsapp } from "react-icons/fa";
import { publicHref } from "../lib/utils/publicHost";
import { FiCheck, FiMapPin, FiRefreshCw, FiClock, FiUser, FiCreditCard } from "react-icons/fi";
import {
  getPublicBookingDetail,
  getPublicBookingQueue,
  cancelPublicBooking,
  bookingPageTokenKey,
  PublicBookingDetail,
  PublicBookingQueue,
  ManageBlockedReason,
} from "../lib/apis/publicBooking";

const fmtTime = (t: string | null | undefined): string => {
  if (!t) return "";
  const [h, m] = String(t).split(":");
  const hour = Number(h);
  if (Number.isNaN(hour)) return String(t);
  const suffix = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m ?? "00"} ${suffix}`;
};
const STATUS_LABEL: Record<number, string> = { 0: "Booked", 1: "Checked in", 2: "In consultation", 3: "Completed", 4: "Cancelled", 5: "Timed out" };
const tokenKey = bookingPageTokenKey;
const money = (amount?: number | null, currency?: string) =>
  amount == null ? "" : currency && currency !== "INR" ? `${currency} ${amount}` : `₹${amount}`;
// Why an action is not offered (the server's reason codes).
const BLOCKED_TEXT: Record<ManageBlockedReason, string> = {
  disabled: "Changes aren't available online for this hospital. Please contact them.",
  status: "This appointment can no longer be changed online. Please contact the reception.",
  past: "This appointment has passed.",
  cutoff: "It's too close to your appointment to change it online. Please contact the hospital.",
  payment_pending: "Complete the payment first. An unpaid booking is released automatically.",
};

const PublicBookingAction: React.FC<{ mode: "status" | "manage" }> = ({ mode: initialMode }) => {
  const { hospital_id = "", booking_id = "" } = useParams<{ hospital_id: string; booking_id: string }>();
  const location = useLocation();
  // The one WhatsApp button lands here; the three actions live on this page as
  // a segmented row (queue status / manage switch views, directions is a link).
  const [mode, setMode] = useState<"status" | "manage">(initialMode);
  const [token, setToken] = useState<string | null>(null);
  const [data, setData] = useState<PublicBookingDetail | null>(null);
  // Live queue (status view): same source and rules as the TV screen.
  const [queue, setQueue] = useState<PublicBookingQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  // Cancel: a confirm popup with an optional reason.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Token from the link, then remembered per tab; the address bar is cleaned.
  useEffect(() => {
    try {
      const q = new URLSearchParams(location.search);
      const t = (q.get("t") || "").trim();
      if (t) {
        try { window.sessionStorage.setItem(tokenKey(booking_id), t); } catch { /* storage blocked */ }
        q.delete("t");
        window.history.replaceState(null, "", `${location.pathname}${q.toString() ? `?${q.toString()}` : ""}`);
        setToken(t);
        return;
      }
      const stored = window.sessionStorage.getItem(tokenKey(booking_id));
      setToken(stored || null);
    } catch {
      setToken(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking_id]);

  const load = async (t: string) => {
    setLoading(true);
    try {
      const [d, q] = await Promise.all([
        getPublicBookingDetail(booking_id, hospital_id, t),
        getPublicBookingQueue(booking_id, hospital_id, t).catch(() => null),
      ]);
      setData(d);
      setQueue(q);
      setError(null);
      setRefreshedAt(new Date());
    } catch (e: any) {
      const msg = e?.response?.data?.error;
      setError(typeof msg === "string" ? msg : "Could not load this booking.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (token === null) return;
    if (!token) {
      setError("This link needs to be opened from your WhatsApp confirmation.");
      setLoading(false);
      return;
    }
    load(token);
    if (mode !== "status") return;
    // Queue status: every 30 s while the tab is open, and at once when the
    // patient comes back to it (phones pause timers in background tabs).
    const id = setInterval(() => {
      if (document.visibilityState !== "hidden") load(token);
    }, 30 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") load(token);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, mode]);

  const nameWithPrefix = (name?: string | null, prefix?: boolean | null) =>
    name ? `${prefix === false || /^dr\.?\s/i.test(name) ? "" : "Dr. "}${name}` : "";
  // The doctor who holds the booking now; the original is shown as "transferred from".
  const originalDoctor = data ? nameWithPrefix(data.doctor_name, data.doctor_show_name_prefix) : "";
  const doctor = data && data.transferred && data.current_doctor_name
    ? nameWithPrefix(data.current_doctor_name, data.current_doctor_show_name_prefix)
    : originalDoctor;
  const doctorSpecialization = data && data.transferred && data.current_doctor_name ? data.current_doctor_specialization : data?.doctor_specialization;
  const when = data ? moment(data.booked_date).format("ddd, D MMM YYYY") : "";
  const time = data
    ? data.time_block_start && data.time_block_end
      ? `${fmtTime(data.time_block_start)} - ${fmtTime(data.time_block_end)}${data.token_number ? ` · Token ${data.token_number}` : ""}`
      : data.token_number
        ? `Token ${data.token_number} · ${fmtTime(data.booking_session_start_time)} - ${fmtTime(data.booking_session_end_time)}`
        : data.booked_slot_start
          ? `${fmtTime(data.booked_slot_start)} - ${fmtTime(data.booked_slot_end)}`
          : `${fmtTime(data.booking_session_start_time)} - ${fmtTime(data.booking_session_end_time)}`
    : "";
  // An old link opens the booking as it is now: say what happened to it.
  const cancellation = data && data.status === 4 ? data.cancellation || null : null;
  const reschedule = data?.reschedule || null;
  const fmtAt = (at?: string | null) => (at ? moment(at, "YYYY-MM-DD HH:mm").format("ddd D MMM, h:mm A") : "");
  const rescheduledFrom = reschedule
    ? [
        moment(reschedule.from.booked_date).format("ddd, D MMM YYYY"),
        reschedule.from.token_number && data?.token_number
          ? `Token ${reschedule.from.token_number}`
          : reschedule.from.booked_slot_start && reschedule.from.booked_slot_start !== reschedule.from.booking_session_start_time
            ? `${fmtTime(reschedule.from.booked_slot_start)} - ${fmtTime(reschedule.from.booked_slot_end)}`
            : reschedule.from.booking_session_start_time
              ? `${fmtTime(reschedule.from.booking_session_start_time)} - ${fmtTime(reschedule.from.booking_session_end_time)}`
              : "",
        reschedule.from.doctor_name ? nameWithPrefix(reschedule.from.doctor_name, reschedule.from.doctor_show_name_prefix) : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
  const manage = data?.manage || null;
  const payment = data?.payment;
  // Same tab: Razorpay brings the patient back to this booking's status page.
  const payNow = payment?.state === "pending" && payment.link ? payment.link : null;
  const goReschedule = () => {
    window.location.href = publicHref(`/${hospital_id}?reschedule=${encodeURIComponent(booking_id)}`);
  };
  const confirmCancel = async () => {
    if (!token) return;
    setCancelling(true);
    setActionError(null);
    try {
      await cancelPublicBooking(booking_id, hospital_id, token, cancelReason.trim() || undefined);
      setCancelOpen(false);
      setCancelReason("");
      setNotice("Your appointment is cancelled. A confirmation is on its way to WhatsApp.");
      await load(token);
    } catch (e: any) {
      const msg = e?.response?.data?.error;
      setActionError(typeof msg === "string" ? msg : "Could not cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  };
  const waHref = data?.hospital_contact_number ? `https://wa.me/${String(data.hospital_contact_number).replace(/\D/g, "")}` : "";

  return (
    <div className="min-h-screen bg-[#F7F8FC] px-4 py-6 flex items-start justify-center">
      <div className="w-full max-w-md">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0F3CBE]">Your appointment</p>
        <h1 className="mt-1 text-xl font-bold text-modalText">{data?.hospital_name || "Your appointment"}</h1>

        {data && (
          <div className="mt-4 grid grid-cols-3 gap-2">
            {(["status", "manage"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  window.history.replaceState(null, "", `/${hospital_id}/${m}/${booking_id}${location.search}`);
                }}
                aria-pressed={mode === m}
                className={`h-11 rounded-lg border text-[13px] font-semibold transition-colors ${
                  mode === m ? "bg-[#0F3CBE] border-[#0F3CBE] text-white" : "bg-white border-doctorsBorder text-modalText hover:border-[#0F3CBE]"
                }`}
              >
                {m === "status" ? "Queue status" : "Manage booking"}
              </button>
            ))}
            {data.directions_url ? (
              <a
                href={data.directions_url}
                target="_blank"
                rel="noreferrer"
                className="h-11 inline-flex items-center justify-center gap-1 rounded-lg border border-doctorsBorder bg-white text-[13px] font-semibold text-modalText hover:border-[#0F3CBE]"
              >
                <FiMapPin className="w-3.5 h-3.5" /> Directions
              </a>
            ) : (
              <span className="h-11 inline-flex items-center justify-center rounded-lg border border-doctorsBorder bg-[#F7F7F9] text-[13px] text-queueText">No directions</span>
            )}
          </div>
        )}

        {loading && !data && <p className="mt-6 text-sm text-queueText">Loading…</p>}
        {error && !data && (
          <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">{error}</div>
        )}

        {data && (
          <>
            {notice && (
              <div className="mt-4 rounded-xl border border-[#1FAD63]/40 bg-[#EAF8F0] px-4 py-3 text-sm font-semibold text-[#14804A]">{notice}</div>
            )}
            {cancellation && !notice && (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-semibold text-red-700">This appointment was cancelled</p>
                <p className="mt-1 text-[12px] text-red-700/90">
                  {cancellation.by === "patient" ? "You cancelled it online" : `Cancelled by ${data.hospital_name || "the hospital"}`}
                  {cancellation.at ? ` on ${fmtAt(cancellation.at)}` : ""}.
                  {cancellation.reason ? ` Reason: “${cancellation.reason}”.` : ""}
                </p>
                <p className="mt-1 text-[12px] text-red-700/90">To see the doctor, book a new appointment{cancellation.by === "hospital" ? " or contact the hospital" : ""}.</p>
              </div>
            )}
            {reschedule && !cancellation && (
              <div className="mt-4 rounded-2xl border border-[#0F3CBE]/25 bg-[#F5F8FF] p-4">
                <p className="text-sm font-semibold text-[#0F3CBE]">
                  This appointment was rescheduled{reschedule.times > 1 ? ` (${reschedule.times} times)` : ""}
                </p>
                <p className="mt-1 text-[12px] text-modalText">
                  <span className="text-queueText">Was:</span> <span className="line-through decoration-queueText/60">{rescheduledFrom}</span>
                </p>
                <p className="text-[12px] text-modalText">
                  <span className="text-queueText">Now:</span> <span className="font-semibold">{[when, time].filter(Boolean).join(" · ")}</span>
                </p>
                {reschedule.at && <p className="mt-1 text-[11px] text-queueText">Changed on {fmtAt(reschedule.at)}</p>}
              </div>
            )}
            {payment?.state === "pending" && (
              <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-900">Payment pending{payment.amount != null ? ` · ${money(payment.amount, payment.currency)}` : ""}</p>
                <p className="mt-1 text-[12px] text-amber-800">
                  Your appointment is held until you pay{payment.expires_at ? ` (by ${moment(payment.expires_at).format("h:mm A")})` : ""}. It's confirmed on WhatsApp once paid.
                </p>
                {payNow ? (
                  <a href={payNow} className="mt-3 h-11 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0F3CBE] text-white text-sm font-semibold">
                    <FiCreditCard className="w-4 h-4" /> Pay {money(payment.amount, payment.currency)}
                  </a>
                ) : (
                  <p className="mt-2 text-[12px] text-amber-800">The payment link isn't available. Please contact the hospital.</p>
                )}
              </div>
            )}
            {payment?.state === "expired" && data.status !== 0 && (
              <div className="mt-4 rounded-xl border border-doctorsBorder bg-white px-4 py-3 text-[13px] text-queueText">
                The payment window closed, so this booking was released. Please book again.
              </div>
            )}
            {mode === "status" && (
              <div className="mt-4 rounded-2xl border border-doctorsBorder bg-white overflow-hidden">
                <div className="p-5 text-center">
                  {queue?.transferred && queue.doctor?.name && (
                    <p className="mb-3 inline-flex items-center rounded-full bg-[#EAF0FF] px-3 py-1 text-[11px] font-semibold text-[#0F3CBE]">
                      Transferred to {nameWithPrefix(queue.doctor.name)}{queue.transferred_from ? ` · from ${nameWithPrefix(queue.transferred_from)}` : ""}
                    </p>
                  )}
                  {/* Today: is the doctor in, on a break, or not here yet (the old "Dr. X has arrived" message). */}
                  {queue && queue.doctor && queue.doctor.in_clinic != null && ["not_checked_in", "in_queue", "in_consultation"].includes(queue.state) && (
                    <div
                      className={`mb-3 rounded-xl px-3 py-2 text-left text-[12px] ${
                        queue.doctor.paused
                          ? "bg-amber-50 border border-amber-200 text-amber-900"
                          : queue.doctor.in_clinic
                            ? "bg-[#EAF8F0] border border-[#1FAD63]/30 text-[#14804A]"
                            : "bg-[#F7F7F9] border border-doctorsBorder text-queueText"
                      }`}
                    >
                      <p className="font-semibold">
                        {queue.doctor.paused
                          ? `${doctor || nameWithPrefix(queue.doctor.name)} is on a short break`
                          : queue.doctor.in_clinic
                            ? `${doctor || nameWithPrefix(queue.doctor.name)} is in the clinic`
                            : `${doctor || nameWithPrefix(queue.doctor.name)} hasn't arrived yet`}
                      </p>
                      <p className="mt-0.5">
                        {queue.doctor.paused
                          ? queue.doctor.pause_reason || "Consultations resume shortly."
                          : queue.doctor.in_clinic
                            ? `Arrived${queue.doctor.arrived_at ? ` at ${fmtTime(queue.doctor.arrived_at)}` : ""} · ${queue.in_clinic_count ?? 0} patient(s) waiting in the clinic`
                            : "This page updates as soon as the doctor checks in."}
                      </p>
                    </div>
                  )}
                  {queue && queue.emergency && ["not_checked_in", "in_queue"].includes(queue.state) && (
                    <p className="mb-3 inline-flex items-center rounded-full bg-red-50 border border-red-200 px-3 py-1 text-[11px] font-semibold text-red-700">
                      Marked as emergency · you'll be seen on priority
                    </p>
                  )}
                  {queue && queue.on_hold && ["not_checked_in", "in_queue"].includes(queue.state) && (
                    <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left text-[12px] text-amber-900">
                      <p className="font-semibold">Your appointment is on hold</p>
                      <p className="mt-0.5">The reception has put your turn on hold for now. Please stay nearby; they'll call you when you're back in the queue.</p>
                    </div>
                  )}
                  {!queue ? (
                    <p className="text-sm text-queueText">{loading ? "Loading the queue…" : "Queue status is not available right now."}</p>
                  ) : queue.state === "not_today" ? (
                    <>
                      <p className="text-sm font-semibold text-modalText">Queue status opens on your appointment day</p>
                      <p className="mt-1 text-[12px] text-queueText">
                        Your appointment is on {moment(queue.booked_date).format("ddd, D MMM YYYY")}. Come back here that day to see your place in the queue.
                      </p>
                    </>
                  ) : queue.state === "cancelled" ? (
                    <p className="text-sm font-semibold text-red-600">
                      This appointment was cancelled{cancellation?.at ? ` on ${fmtAt(cancellation.at)}` : ""}.
                    </p>
                  ) : queue.state === "completed" ? (
                    <p className="text-sm font-semibold text-[#1FAD63]">Your consultation is complete. Thank you for visiting.</p>
                  ) : queue.state === "timeout" ? (
                    <>
                      <p className="text-sm font-semibold text-[#B45309]">Your token timed out.</p>
                      <p className="mt-1 text-[12px] text-queueText">Please speak to the reception to rejoin the queue.</p>
                    </>
                  ) : queue.state === "not_checked_in" ? (
                    <>
                      {queue.ahead_booked != null && (
                        <>
                          <p className="text-[12px] text-queueText">Patients ahead of you</p>
                          <p className="mt-1 text-5xl font-bold text-[#0F3CBE]">{queue.ahead_booked}</p>
                          <p className="mt-1 mb-3 text-[12px] text-queueText">
                            {queue.token_number != null ? `Your token number is ${queue.token_number} · ` : ""}
                            {queue.in_clinic_count ?? 0} checked in and waiting now
                          </p>
                        </>
                      )}
                      <p className="text-sm font-semibold text-[#B45309]">You're not checked in yet</p>
                      <p className="mt-1 text-[12px] text-queueText">
                        Please check in at the reception when you arrive. The count above is by booking order; your exact place is set once you check in.
                      </p>
                      {queue.now_serving && queue.now_serving.length > 0 && (
                        <p className="mt-2 text-[12px] text-queueText">Now serving token {queue.now_serving.join(", ")}</p>
                      )}
                    </>
                  ) : queue.state === "in_consultation" ? (
                    <>
                      <p className="text-sm font-semibold text-[#0F3CBE]">It's your turn</p>
                      <p className="mt-1 text-[12px] text-queueText">
                        Please proceed to the consultation room{queue.queue?.find((r) => r.is_you)?.room_name ? ` · ${queue.queue.find((r) => r.is_you)?.room_name}` : ""}.
                      </p>
                    </>
                  ) : (
                    <>
                      {queue.ahead === 0 && !queue.on_hold && (
                        <p className="mb-2 text-sm font-semibold text-[#1FAD63]">You're next. Please be ready to see the doctor.</p>
                      )}
                      <p className="text-[12px] text-queueText">Patients ahead of you</p>
                      <p className="mt-1 text-5xl font-bold text-[#0F3CBE]">{queue.ahead ?? "—"}</p>
                      <p className="mt-1 text-[12px] text-queueText">
                        {queue.token_number != null ? `Your token number is ${queue.token_number}` : ""}
                        {queue.now_serving && queue.now_serving.length > 0 ? ` · Now serving token ${queue.now_serving.join(", ")}` : ""}
                      </p>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => token && load(token)}
                    className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0F3CBE]"
                  >
                    <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    Refresh{refreshedAt ? ` · ${moment(refreshedAt).format("h:mm A")}` : ""}
                  </button>
                  <p className="mt-1 text-[10px] text-queueText">Updates on its own every 30 seconds</p>
                </div>

                {/* The live list, only for the states the TV would show (status 1–9 today). */}
                {queue && (queue.state === "in_queue" || queue.state === "in_consultation") && (queue.queue?.length ?? 0) > 0 && (
                  <div className="border-t border-doctorsBorder">
                    <div className="px-4 py-2 flex items-center justify-between text-[11px] uppercase tracking-[0.12em] text-queueText">
                      <span>{queue.doctor?.name ? doctor || queue.doctor.name : "Queue"}</span>
                      <span>
                        {queue.session?.start_time ? `${fmtTime(queue.session.start_time)} - ${fmtTime(queue.session.end_time)}` : ""}
                      </span>
                    </div>
                    <ul className="divide-y divide-doctorsBorder max-h-80 overflow-y-auto">
                      {queue.queue!.map((r) => (
                        <li
                          key={`${r.position}-${r.token_number}`}
                          className={`flex items-center gap-3 px-4 py-2 ${r.is_you ? "bg-[#EAF0FF]" : ""}`}
                        >
                          <span className={`shrink-0 min-w-[64px] text-center text-[12px] font-semibold rounded-md px-2 py-1 ${r.ongoing ? "bg-[#10B018] text-white" : "bg-[#0F3CBE] text-white"}`}>
                            {r.token_number != null ? `Token ${r.token_number}` : `#${r.position}`}
                          </span>
                          <span className={`flex-1 min-w-0 truncate text-sm ${r.is_you ? "font-bold text-modalText" : "text-queueText"}`}>
                            {r.name}
                            {r.is_you && <span className="ml-2 text-[10px] font-bold uppercase text-[#0F3CBE]">You</span>}
                            {r.emergency && <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700">Emergency</span>}
                            {r.on_hold && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">On hold</span>}
                          </span>
                          <span className={`shrink-0 text-[11px] font-semibold ${r.ongoing ? "text-[#10B018]" : "text-queueText"}`}>
                            {r.ongoing ? "In consultation" : r.category}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-doctorsBorder bg-white overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-[#0F3CBE] via-[#3D5AE0] to-[#0F3CBE]" />
              <dl className="divide-y divide-doctorsBorder text-sm">
                {[
                  ["Patient", data.patient_name || "—", <FiUser key="u" className="w-3.5 h-3.5" />],
                  ["Doctor", [doctor, doctorSpecialization].filter(Boolean).join(" · ") || "—", null],
                  ...(data.transferred && originalDoctor ? [["Transferred from", originalDoctor, null] as const] : []),
                  // One row, same as the booking confirmation: date · the patient's time.
                  ["When", [when, time].filter(Boolean).join(" · ") || "—", <FiClock key="c" className="w-3.5 h-3.5" />],
                  ["Status", STATUS_LABEL[data.status] || String(data.status), <FiCheck key="s" className="w-3.5 h-3.5" />],
                  ...(payment?.state === "paid" ? [["Fee", `${money(payment.amount, payment.currency)} paid`, null] as const] : []),
                ].map(([k, v, icon]) => (
                  <div key={String(k)} className="flex items-start justify-between gap-3 px-4 py-2.5">
                    <dt className="flex items-center gap-1.5 text-queueText">{icon}{k as string}</dt>
                    <dd className="text-right font-semibold text-modalText">{v as string}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {mode === "manage" && data.status === 4 && !notice && !cancellation && (
              <div className="mt-4 rounded-2xl border border-doctorsBorder bg-white p-4 text-sm text-queueText">
                This appointment is cancelled. To see the doctor, book a new appointment.
              </div>
            )}
            {mode === "manage" && data.status !== 4 && (
              <div className="mt-4 rounded-2xl border border-doctorsBorder bg-white p-4">
                <p className="text-sm font-semibold text-modalText">Need to change something?</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={!manage?.can_reschedule}
                    onClick={goReschedule}
                    className="h-11 rounded-lg border border-[#0F3CBE] text-sm font-semibold text-[#0F3CBE] hover:bg-[#EAF0FF] disabled:border-doctorsBorder disabled:text-queueText disabled:opacity-60 disabled:hover:bg-transparent"
                  >
                    Reschedule
                  </button>
                  <button
                    type="button"
                    disabled={!manage?.can_cancel}
                    onClick={() => {
                      setActionError(null);
                      setCancelOpen(true);
                    }}
                    className="h-11 rounded-lg border border-red-300 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:border-red-200 disabled:text-red-300 disabled:opacity-60 disabled:hover:bg-transparent"
                  >
                    Cancel booking
                  </button>
                </div>
                {(() => {
                  // Under the buttons: per action, why it's off, else until when it's open.
                  // Reschedule and cancel have separate limits, so each gets its own line.
                  if (!manage) return <p className="mt-2 text-[11px] text-queueText">Changes aren't available right now. Please contact the hospital.</p>;
                  const r = manage.reschedule_blocked_reason;
                  const c = manage.cancel_blocked_reason;
                  if (r === "disabled" && c === "disabled") return <p className="mt-2 text-[11px] text-queueText">{BLOCKED_TEXT.disabled}</p>;
                  const lines: string[] = [];
                  if (r && r !== "disabled") lines.push(BLOCKED_TEXT[r]);
                  else if (manage.can_reschedule && manage.reschedule_deadline)
                    lines.push(`You can reschedule online until ${moment(manage.reschedule_deadline).format("ddd D MMM, h:mm A")}.`);
                  if (c && c !== "disabled") lines.push(BLOCKED_TEXT[c]);
                  else if (manage.can_cancel && manage.deadline)
                    lines.push(`You can cancel online until ${moment(manage.deadline).format("ddd D MMM, h:mm A")}.`);
                  const unique = lines.filter((l, i) => lines.indexOf(l) === i);
                  if (!unique.length) return null;
                  return (
                    <div className="mt-2 flex flex-col gap-0.5">
                      {unique.map((l) => (
                        <p key={l} className="text-[11px] text-queueText">{l}</p>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2">
              {waHref && (
                <a href={waHref} className="h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-[#1FAD63] text-white text-sm font-semibold">
                  <FaWhatsapp className="w-4 h-4" /> Back to WhatsApp
                </a>
              )}
            </div>
            {data.hospital_address && <p className="mt-3 text-[12px] text-queueText text-center">{data.hospital_address}</p>}
          </>
        )}
        <p className="mt-6 text-center text-[11px] text-queueText">Powered by DocTrue</p>

        {cancelOpen && data && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4 pb-4 sm:pb-0" role="dialog" aria-modal="true">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <p className="text-base font-bold text-modalText">Cancel this appointment?</p>
              <p className="mt-1 text-[13px] text-queueText">
                {[doctor, when, time].filter(Boolean).join(" · ")}
              </p>
              {manage?.paid && (
                <p className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900">
                  The {money(payment?.amount, payment?.currency) || "booking"} fee you paid is non-refundable.
                </p>
              )}
              <label className="mt-3 block text-[12px] font-semibold text-modalText" htmlFor="cancel-reason">Reason (optional)</label>
              <input
                id="cancel-reason"
                type="text"
                maxLength={120}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Feeling better, travelling"
                className="mt-1 w-full h-11 rounded-lg border border-doctorsBorder px-3 text-sm focus:outline-none focus:border-[#0F3CBE]"
              />
              {actionError && <p className="mt-2 text-[12px] text-red-600">{actionError}</p>}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCancelOpen(false)}
                  disabled={cancelling}
                  className="h-11 rounded-lg border border-doctorsBorder text-sm font-semibold text-modalText"
                >
                  Keep it
                </button>
                <button
                  type="button"
                  onClick={confirmCancel}
                  disabled={cancelling}
                  className="h-11 rounded-lg bg-red-600 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {cancelling ? "Cancelling…" : "Yes, cancel"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicBookingAction;
